import { Box, Tab, Tabs } from '@mui/material';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { PageHeader } from '../../components/states';

interface TabDef {
  label: string;
  path: string;
  show?: (can: (p: string) => boolean) => boolean;
}

const TABS: TabDef[] = [
  { label: 'Stock on hand', path: '/inventory' },
  { label: 'Movements', path: '/inventory/movements' },
  { label: 'Adjustments', path: '/inventory/adjustments' },
  { label: 'Stock counts', path: '/inventory/counts' },
  {
    label: 'Opening stock',
    path: '/inventory/opening',
    show: (can) => can('inventory.opening_stock'),
  },
  { label: 'Locations', path: '/inventory/locations' },
];

/** The inventory hub: one header and a tab strip; each tab is its own route (deep-linkable). */
export function InventoryLayout({
  subtitle,
  actions,
  children,
}: {
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const tabs = TABS.filter((t) => !t.show || t.show(can));
  const current =
    [...tabs]
      .sort((a, b) => b.path.length - a.path.length)
      .find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`))?.path ?? false;

  return (
    <>
      <PageHeader
        title="Inventory"
        subtitle={
          subtitle ?? 'Stock on hand, movements, adjustments and counts across your locations.'
        }
        actions={actions}
      />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, mt: -1 }}>
        <Tabs
          value={current}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Inventory sections"
          sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, fontWeight: 600 } }}
        >
          {tabs.map((t) => (
            <Tab key={t.path} value={t.path} label={t.label} component={Link} to={t.path} />
          ))}
        </Tabs>
      </Box>
      {children}
    </>
  );
}
